<template>
  <BaseForm :loading="loading" class="noprogress" :back="onBack">
    <template v-slot:header>
      {{ $t(`renew_selectpolicy_header.${header}`) }}
    </template>
    <template v-if="selectedPolicy" v-slot:logo>
      <RenewLogo
        :firstLineLogo="$t(`logo_${productType}1`)"
        :secondLineLogo="$t(`logo_${productType}2`)"
        :class="`${productType}-${locale}`"
        :appName="productType"/>
    </template>
    <div class="selectPolicyGrid">
      <template v-if="!selectedPolicy">
        <SelectPolicyCard
          v-for="(policy, index) in sortedPolicies"
          :key="index"
          :policy="policy"
          cursor
          @click="selectPolicy(policy)"/>
        <BaseButton :symbol="'no'" type="button" @click="goToChooseNewPolicy" class="skipBtn">
          {{ $t('renew_another_policy') }}
        </BaseButton>
      </template>
      <template v-else>
        <SelectPolicyCard :policy="selectedPolicy" />
        <PromoInputWithApply
          v-model="promocode"
          :validation="promocodeValidation.bind(this)"/>
        <BaseButton type="button" @click="onSubmit"/>
      </template>
    </div>
  </BaseForm>
</template>

<script>
import { BaseForm, PromoInputWithApply, RenewLogo } from 'qic-components'
import prodCodeMap from '@/assets/maps/prodCodeMap.json'
import SelectPolicyCard from '@/components/SelectPolicyCard'
import { POLICY_CASES } from '@/constants/policyConstants'
import { trackGtag } from '@/utils/useGtag'
import getPolicyProductType from '@/shared/policy/getPolicyProductType'
import { getErrorMessage } from '@/shared/errors/getErrorMessage'

export default {
  name: 'SelectPolicy',
  props: {
    phone: { type: String },
    qid: { type: String },
    policy: { type: String },
    makeCode: { type: String },
    modelCode: { type: String },
    transId: { type: String }
  },
  components: {
    BaseForm,
    PromoInputWithApply,
    RenewLogo,
    SelectPolicyCard
  },
  data () {
    return {
      policies: [],
      filter: this.$store.state.renew.filter || null,
      loading: false,
      prodCodesMap: prodCodeMap,
      selectedPolicy: null,
      productType: this.$store.state.renew.productType || '',
      promocode: this.$store.state.renew.promocode,
      header: 'base'
    }
  },
  computed: {
    submitDisabled () {
      return this.$v.$invalid
    },
    locale () {
      return this.$i18n.locale
    },
    sortedPolicies () {
      if (!Array.isArray(this.policies)) return [] /* @TODO убрать */
      const policies = [...this.policies]
      return policies.sort((a, b) => {
        return a.polEndDate - b.polEndDate
      })
    },
    product () {
      if (!this.selectedPolicy) return null
      return prodCodeMap.find((product) => product.code === this.selectedPolicy?.prodCode) || null
    },
    policyName () {
      if (!this.product) return ''
      return this.$te(this.product?.nameT) ? this.$t(this.product.nameT) : this.product.name
    },
    policyEndDate () {
      return this.selectedPolicy?.polEndDate ? new Date(this.selectedPolicy.polEndDate) : null
    },
    formattedEndDate () {
      if (!this.policyEndDate) return null

      const day = String(this.policyEndDate.getDate()).padStart(2, '0')
      const month = String(this.policyEndDate.getMonth() + 1).padStart(2, '0')
      const year = this.policyEndDate.getFullYear()

      return `${day}.${month}.${year}`
    },
    filtredCodeList () {
      return [...this.prodCodesMap
        .filter(policy => policy.type === this.filter)
        .reduce((acc, cur) => {
          return [...acc, cur.code]
        }, [])]
    }
  },
  methods: {
    onSubmit () {
      this.$store.commit('setPromocode', this.promocode)
      if (this.isCarPolicy(this.selectedPolicy.prodCode)) {
        this.$router.push({ name: 'WizardRenewMotor', params: { page: 'info' } })
        return
      }
      this.$router.push({ name: 'SelectPlan' })
    },
    getPolicies (params) {
      return this.$store.dispatch('getActivePolicies', params)
        .then((policies) => {
          this.policies = policies

          if (this.filter) {
            this.policies = this.policies.filter(policy => this.filtredCodeList.includes(policy.prodCode))
          }
          if (!this.policies.length) {
            this.$router.replace({
              name: 'PolicyNotFoundLink',
              params: { policyCase: POLICY_CASES.BY_EXTERNAL_LINK }
            })

            return
          }

          if (this.policies.length === 1) {
            const policyCase = this.policies[0].state
            this.saveInfoAboutSelectedPolicy(this.policies[0])

            const validCases = [1, 2, 3]
            const pageNameByCase = {
              1: 'PolicyActiveSoon',
              2: 'PolicyAlreadyRenewed',
              3: 'PolicyActive'
            }

            if (validCases.includes(policyCase)) {
              this.$router.replace({
                name: pageNameByCase[policyCase],
                params: {
                  policyCase,
                  polEndDate: this.policies[0].polEndDate,
                  polStartDate: this.policies[0].polStartDate
                }
              })
              return
            }

            if (this.isCarPolicy(this.selectedPolicy.prodCode)) {
              this.$router.replace({ name: 'WizardRenewMotor', params: { page: 'info' } })
              return
            }
            this.$router.replace({ name: 'SelectPlan' })
          }
          if (this.policies.every(policy => policy.renwAllowYN === '0')) {
            this.$router.replace({
              name: 'NoPoliciesList',
              params: { policyCase: POLICY_CASES.NO_RENEWAL_ALLOWED }
            })
          }
        })
    },
    selectPolicy (policy) {
      this.saveInfoAboutSelectedPolicy(policy)
      trackGtag('select_policy', {
        page: 'select_policy',
        policy_id: policy.policyNo,
        value: this.$store.state.renew.userData.phone,
        plan_name: this.policyName === 'Third Party Liability Car Insurance' ? 'TPL' : 'OD',
        end_date: this.formattedEndDate
      })
      this.onSubmit()
    },
    saveInfoAboutSelectedPolicy (policy) {
      const type = getPolicyProductType(policy)
      this.productType = type
      this.$store.commit('setProductType', type)
      this.$store.commit('setPolicyInfo', policy)
      policy.civilId && this.$store.commit('setUserQid', policy.civilId)
      this.selectedPolicy = policy
    },
    goToChooseNewPolicy () {
      trackGtag('click_another_policy', {
        page: 'select_policy',
        value: this.$store.state.renew.userData.phone
      })
      this.$router.push({ name: 'PolicyNumber' })
    },
    onBack () {
      if (this.selectedPolicy && this.policies.length > 1) {
        this.selectedPolicy = null
      } else {
        this.$router.back()
      }
    },
    isCarPolicy (productCode) {
      const carProductCodes = ['0100', '0110']
      return carProductCodes.includes(productCode)
    },
    promocodeValidation (promocode) {
      return this.$store.dispatch('isPromocdoeValid', { promocode, type: 'OD' })
    }
  },
  mounted () {
    this.loading = true
    const params = {}
    if (this.$store.state.renew.byUrl) params.renewalbylink = 1

    const storeUserData = this.$store.state.renew.userData
    const phoneStore = storeUserData.phone
    const policyStore = storeUserData.policy
    const makeCodeStore = storeUserData.makeCode
    const modelCodeStore = storeUserData.modelCode

    const qid = this.qid || storeUserData.qid
    const transId = this.transId || storeUserData.transId

    if (qid) {
      this.$store.commit('setUserQid', qid)
    } else {
      this.$store.commit('setUserTransId', transId)
    }

    if (!(qid || transId) && (!makeCodeStore || !modelCodeStore)) {
      this.$router.replace({ name: 'NotFound', params: { message: this.$t('no_policy_error') } })
      return
    }
    if (this.$route.params.transId) {
      params.transId = transId
    } else {
      params.qatarid = qid
    }

    if (this.policy) {
      params.policyNo = this.policy
      params.makeCode = makeCodeStore
      params.modelCode = modelCodeStore
      this.$store.commit('setUserPolicy', this.policy)
    } else if (this.phone) {
      params.mobileNo = this.phone
      this.$store.commit('setUserPhone', this.phone)
    } else if (policyStore && phoneStore) {
      this.getPolicies({ mobileNo: phoneStore, qatarid: qid })
        .catch(() => {
          return this.getPolicies({ policyNo: policyStore, qatarid: qid })
        })
        .catch(() => {
          this.$router.replace({ name: 'PolicyNotFound' })
        })
        .finally(() => {
          this.loading = false
        })
      return
    } else if (makeCodeStore && modelCodeStore && policyStore) {
      this.getPolicies({ policyNo: policyStore, makeCode: makeCodeStore, modelCode: modelCodeStore })
        .catch(() => {
          this.$router.replace({ name: 'PolicyNotFound' })
        })
        .finally(() => {
          this.loading = false
        })
      return
    } else if (policyStore) {
      params.policyNo = policyStore
    } else if (phoneStore) {
      params.mobileNo = phoneStore
    } else {
      this.$router.replace({ name: 'NotFound', params: { message: this.$t('no_policy_error') } })
      return
    }

    this.getPolicies(params)
      .catch((error) => {
        const isMobileQidNotFound = (params.mobileNo || !this.phone || phoneStore) && !params.policyNo && params.qatarid
        let propsNotFound = {}

        const errorMessage = getErrorMessage(error)

        if (errorMessage === 'Invalid information') {
          propsNotFound = {
            policyCase: POLICY_CASES.BY_EXTERNAL_LINK
          }
        }
        this.$router.replace({ name: isMobileQidNotFound ? 'MobileQidNotFound' : 'PolicyNotFound', params: propsNotFound })
      })
      .finally(() => {
        trackGtag('view_select_policy', {
          page: 'select_policy',
          value: storeUserData.phone
        })
        this.loading = false
      })
  },
  watch: {
    selectedPolicy (newValue) {
      if (newValue) {
        this.header = 'your_policy'
      } else this.header = 'base'
    }
  }
}
</script>
